// 画像・動画添付の「先に仮表示、あとから本体同期」を支えるヘルパー群。
//
// 仕組みの要点:
// - 添付の本体（blob URL や大きなバイナリ）は WebSocket に載せない。create op の src には
//   軽量なプレースホルダ（画像なら超圧縮した LQIP の data URL、動画なら 1x1 透明 GIF）を入れ、
//   他参加者へ即配信して「誰かが画像を置いた」ことと仮プレビューを伝える。
// - 本体は裏で S3 へアップロードし、完了後に公開 URL を attachment.update で全員へ反映する。
//
// 仮表示中（= src が data: URL）かどうかは isPendingAttachmentSrc で判定する。作成者本人だけが
// 見る blob: URL や、最終的な公開 https URL は「確定済み」として扱い、ローディング表示を出さない。

import type { CanvasObject } from "@refinement-canvas/domain";

// LQIP 生成に失敗した、またはサイズが大きすぎる場合のフォールバック（1x1 透明 GIF）。
// 動画の仮表示にもこれを使う（動画はフレーム抽出を行わずローディング枠のみ表示する）。
export const PENDING_ATTACHMENT_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// LQIP の最長辺ピクセル数。小さいほど data URL が短くなり、ぼかし効果も強くなる。
const LQIP_MAX_DIMENSION = 32;
// 添付 src のサーバ検証は max(8192) のため、それを下回る安全側のしきい値。
// 超える場合は LQIP を諦めて透明プレースホルダにフォールバックする。
const LQIP_MAX_DATA_URL_LENGTH = 8000;
// LQIP を WebP でエンコードする際の品質（0..1）。ぼかし前提なので低めで十分小さくなる。
const LQIP_WEBP_QUALITY = 0.6;

/** 仮表示（アップロード未完了のプレースホルダ）かどうか。data: URL のときに true。 */
export function isPendingAttachmentSrc(src: string): boolean {
  return src.startsWith("data:");
}

/**
 * 他参加者が解決できない一時的な src かどうか。
 * - blob:（作成者本人だけが解決できるローカル原本）
 * - data:（アップロード未完了のプレースホルダ／LQIP）
 * これらはコピー/切り取り/貼り付けでそのまま再配信すると、他参加者で壊れた画像や
 * 永続的な仮表示になるため、確定（公開 URL）前は複製対象から除外する判定に使う。
 */
export function isTransientAttachmentSrc(src: string): boolean {
  return src.startsWith("blob:") || isPendingAttachmentSrc(src);
}

/**
 * 他参加者が解決できない一時的な src を持つ画像・動画かどうか。
 * コピー / 切り取り / 貼り付け / Alt+ドラッグ複製の除外判定で共有する（判定を 1 箇所へ寄せる）。
 */
export function isTransientAttachmentObject(object: CanvasObject): boolean {
  return (
    (object.type === "image" || object.type === "video") && isTransientAttachmentSrc(object.src)
  );
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("画像の読み込みに失敗しました。"));
    img.src = url;
  });
}

/** toDataURL だけを使う最小インターフェース（テストでフェイクを差し込めるようにする）。 */
interface DataUrlEncodableCanvas {
  toDataURL(type?: string, quality?: number): string;
}

/**
 * canvas をアルファチャンネルを保持できる形式で data URL 化する。
 *
 * JPEG はアルファを持てないため、透明 PNG / 透明 WebP / SVG の透明部分が黒く潰れ、
 * 「黒い四角」「黒いにじみ」として見える不具合の原因になる。これを避けるため、
 * アルファを保持できる WebP を優先し、WebP エンコード非対応のブラウザでは PNG へ
 * フォールバックする（どちらもアルファを保持するため黒潰れは起きない）。
 *
 * canvas.toDataURL は WebP 非対応ブラウザでは指定 MIME を無視して PNG を返す。
 * そこで返り値の MIME プレフィックスを見て WebP 化の成否を判定し、失敗時のみ
 * 明示的に PNG を要求する。いずれの経路でも JPEG（アルファ非対応）は使わない。
 */
export function encodeAlphaPreservingDataUrl(canvas: DataUrlEncodableCanvas): string {
  const webp = canvas.toDataURL("image/webp", LQIP_WEBP_QUALITY);
  if (webp.startsWith("data:image/webp")) return webp;
  return canvas.toDataURL("image/png");
}

/**
 * ローカル画像 URL から超圧縮の LQIP（data URL）を生成する。
 * canvas で最長辺 {@link LQIP_MAX_DIMENSION}px へ縮小し、アルファ保持形式（WebP 優先・
 * 非対応ブラウザでは PNG）へエンコードする。透過 PNG / SVG / WebP の透明部分が
 * 黒く潰れないよう、アルファ非対応の JPEG は使わない（{@link encodeAlphaPreservingDataUrl}）。
 * 失敗時やしきい値超過時は {@link PENDING_ATTACHMENT_SRC}（透明プレースホルダ）を返す。
 */
export async function generateImageLqipDataUrl(localUrl: string): Promise<string> {
  try {
    const img = await loadImageElement(localUrl);
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    if (longest === 0) return PENDING_ATTACHMENT_SRC;
    const ratio = Math.min(1, LQIP_MAX_DIMENSION / longest);
    const w = Math.max(1, Math.round(img.naturalWidth * ratio));
    const h = Math.max(1, Math.round(img.naturalHeight * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const context = canvas.getContext("2d");
    if (!context) return PENDING_ATTACHMENT_SRC;
    context.drawImage(img, 0, 0, w, h);
    const dataUrl = encodeAlphaPreservingDataUrl(canvas);
    if (!dataUrl.startsWith("data:image") || dataUrl.length > LQIP_MAX_DATA_URL_LENGTH) {
      return PENDING_ATTACHMENT_SRC;
    }
    return dataUrl;
  } catch {
    return PENDING_ATTACHMENT_SRC;
  }
}
